// Settings tab. The profile pane (name, password) is shown to everyone; the
// admin panes (teams, global settings) render only for admins; a team-lead
// gets a single "My team" pane scoped to their own team. Rendered lazily the
// first time the Settings tab is shown.
import { $, esc, get, post, postStatus, putStatus, loadOrgUsers, state } from './core';
import { restHealth, webhookHealth, webhookUrl, reachableFromJira, type PublicJira } from './connector-status';

// The team currently expanded in the admin pane (null = none). Module-level so a
// re-render after a mutation can restore the open team.
var openTeam: string | null = null;

var loaded = false;
var me: any = null;

// Called by main.ts once /api/auth/me resolves, so the pane knows the current
// user (name to prefill, role to decide which admin panes to show later).
export function initSettings(user) {
  me = user;
}

// Render on first view; cheap to re-run but we only need it once per load.
export function showSettings() {
  if (loaded) return;
  loaded = true;
  render();
}

function render() {
  var root = $('#settings');
  if (!root) return;
  root.innerHTML =
    '<section class="panel">' +
      '<div class="panel-head"><h2>Profile</h2></div>' +
      '<div class="set-grid">' +
        '<label class="set-lbl">Name<input class="set-in" id="set-name" type="text" value="' + esc(me && me.name || '') + '" /></label>' +
        '<div class="set-static"><span class="set-k">Email</span><span class="set-v">' + esc(me && me.email || '') + '</span></div>' +
        '<div class="set-static"><span class="set-k">Role</span><span class="set-v">' + esc(me && me.role || '') + '</span></div>' +
      '</div>' +
      '<div class="set-actions"><button type="button" class="set-btn" id="set-name-btn">Save name</button>' +
        '<span class="set-msg" id="set-name-msg"></span></div>' +
    '</section>' +
    '<section class="panel">' +
      '<div class="panel-head"><h2>Change password</h2></div>' +
      '<div class="set-grid">' +
        '<label class="set-lbl">Current password<input class="set-in" id="set-pw-cur" type="password" autocomplete="current-password" /></label>' +
        '<label class="set-lbl">New password<input class="set-in" id="set-pw-new" type="password" autocomplete="new-password" /></label>' +
      '</div>' +
      '<div class="set-actions"><button type="button" class="set-btn" id="set-pw-btn">Change password</button>' +
        '<span class="set-msg" id="set-pw-msg"></span></div>' +
    '</section>' +
    // Role-gated panes (three-valued now: member sees none, a team-lead gets
    // their own team's management pane, an admin gets the full teams + global
    // panes). Independent containers so one pane's re-render doesn't clobber
    // another. The server re-checks the role on every management route — this
    // gate is UI convenience, not the security boundary.
    (me && me.role === 'admin'
      ? '<div id="set-teams"><div class="empty">Loading teams…</div></div>' +
        '<div id="set-global"><div class="empty">Loading settings…</div></div>' +
        '<div id="set-jira"><div class="empty">Loading connectors…</div></div>'
      : me && me.role === 'team-lead' && me.teamId
        ? '<div id="set-myteam"><div class="empty">Loading team…</div></div>' +
          '<div id="set-jira"></div>'
        : '<div id="set-jira"></div>') +
    '<section class="panel set-signout">' +
      '<button type="button" class="set-btn set-btn-danger" id="set-logout">Sign out</button>' +
    '</section>';

  $('#set-name-btn').onclick = saveName;
  $('#set-pw-btn').onclick = changePassword;
  $('#set-logout').onclick = function () {
    post('/api/auth/logout', {}).then(function () { window.location.reload(); }).catch(function () { window.location.reload(); });
  };
  if (me && me.role === 'admin') { loadAdmin(); loadGlobal(); loadJira(); }
  else {
    renderJiraInfo();
    if (me && me.role === 'team-lead' && me.teamId) loadMyTeam();
  }
}

function saveName() {
  var input = document.getElementById('set-name') as HTMLInputElement;
  var msg = $('#set-name-msg');
  msg.textContent = '';
  postStatus('/api/settings/profile', { name: input.value }).then(function (r) {
    if (r.status >= 200 && r.status < 300) {
      if (me) me.name = r.body && r.body.name;
      msg.textContent = 'Saved.'; msg.className = 'set-msg ok';
    } else { msg.textContent = (r.body && r.body.error) || 'Failed.'; msg.className = 'set-msg err'; }
  }).catch(function () { msg.textContent = 'Network error.'; msg.className = 'set-msg err'; });
}

function changePassword() {
  var cur = document.getElementById('set-pw-cur') as HTMLInputElement;
  var next = document.getElementById('set-pw-new') as HTMLInputElement;
  var msg = $('#set-pw-msg');
  msg.textContent = '';
  postStatus('/api/settings/password', { currentPassword: cur.value, newPassword: next.value }).then(function (r) {
    if (r.status >= 200 && r.status < 300) {
      cur.value = ''; next.value = '';
      msg.textContent = 'Password changed.'; msg.className = 'set-msg ok';
    } else { msg.textContent = (r.body && r.body.error) || 'Failed.'; msg.className = 'set-msg err'; }
  }).catch(function () { msg.textContent = 'Network error.'; msg.className = 'set-msg err'; });
}

// ---- Admin: teams — UI over the /api/admin API (admin-only) -----------------

// One mutation path for every admin action: postStatus, branch on the code,
// reload the whole admin pane on success (so lists stay consistent), surface the
// server error otherwise. Mirrors the sharing panel's mutate() helper.
function adminMutate(url, body, errEl) {
  if (errEl) errEl.textContent = '';
  postStatus(url, body).then(function (r) {
    if (r.status >= 200 && r.status < 300) loadAdmin();
    else if (errEl) errEl.textContent = (r.body && r.body.error) || 'Failed.';
  }).catch(function () { if (errEl) errEl.textContent = 'Network error.'; });
}

function loadAdmin() {
  var root = $('#set-teams');
  if (!root) return;
  // The team expanded when this load began. If it changes (another toggle) or
  // the pane goes away before the fetches resolve, we drop the stale render —
  // the newer loadAdmin() owns the pane. Same stale-guard as loadSharing.
  var want = openTeam;
  Promise.all([get('/api/admin/teams'), get('/api/admin/users')]).then(function (res) {
    var teams = (res[0] && res[0].teams) || [];
    var users = (res[1] && res[1].users) || [];
    var live = function () { return document.getElementById('set-teams') && openTeam === want; };
    // If a team is expanded, fetch its detail before rendering so members/repos
    // are in hand; otherwise render the list directly.
    if (want) {
      get('/api/admin/teams/' + encodeURIComponent(want)).then(function (detail) {
        if (!live()) return;
        if (detail && detail.error) openTeam = null;
        renderAdmin(root, teams, users, detail && detail.error ? null : detail);
      }).catch(function () { if (live()) renderAdmin(root, teams, users, null); });
    } else {
      if (live()) renderAdmin(root, teams, users, null);
    }
  }).catch(function () {
    if (document.getElementById('set-teams') && openTeam === want) root.innerHTML = '<div class="empty">Couldn’t load teams.</div>';
  });
}

function renderAdmin(root, teams, users, detail) {
  var rows = teams.map(function (t) {
    var count = Number(t.memberCount) || 0;
    var head = '<div class="team-row" data-team="' + esc(t.id) + '">' +
      '<button type="button" class="team-name" data-toggle="' + esc(t.id) + '">' + esc(t.name) + '</button>' +
      '<span class="team-meta">' + count + ' member' + (count === 1 ? '' : 's') + '</span>' +
      '<span class="team-acts">' +
        '<button type="button" class="team-rename" data-team="' + esc(t.id) + '" data-name="' + esc(t.name) + '">Rename</button>' +
        '<button type="button" class="team-delete" data-team="' + esc(t.id) + '">Delete</button>' +
      '</span></div>';
    // Only expand when the detail we hold is actually THIS team's — a raced or
    // stale detail must never render under another team's row (its edit buttons
    // would target this row's id while showing the other team's members/repos).
    var showDetail = openTeam === t.id && detail && detail.team && detail.team.id === t.id;
    return head + (showDetail ? teamDetail(detail, users) : '');
  }).join('');

  root.innerHTML =
    '<section class="panel">' +
      '<div class="panel-head"><h2>Teams</h2>' +
        '<button type="button" class="set-btn" id="bs-import" title="Mocked in V0">Import from Backstage</button>' +
      '</div>' +
      '<div class="team-create">' +
        '<input class="set-in" id="team-new" type="text" placeholder="New team name" />' +
        '<button type="button" class="set-btn" id="team-create-btn">Create</button>' +
        '<span class="set-msg err" id="admin-err"></span>' +
      '</div>' +
      '<div class="team-list">' + (rows || '<div class="empty">No teams yet.</div>') + '</div>' +
    '</section>';

  var err = document.getElementById('admin-err');
  var createBtn = document.getElementById('team-create-btn');
  if (createBtn) createBtn.onclick = function () {
    var input = document.getElementById('team-new') as HTMLInputElement;
    var name = (input.value || '').trim();
    if (!name) return;
    adminMutate('/api/admin/teams', { name: name }, err);
  };
  var bs = document.getElementById('bs-import');
  if (bs) bs.onclick = function () {
    postStatus('/api/admin/import-backstage', {}).then(function (r) {
      if (err) { err.textContent = (r.body && r.body.error) || 'Import is mocked in V0.'; err.className = 'set-msg'; }
    }).catch(function () {});
  };

  Array.prototype.forEach.call(root.querySelectorAll('.team-name[data-toggle]'), function (b) {
    b.onclick = function () { var id = b.getAttribute('data-toggle'); openTeam = openTeam === id ? null : id; loadAdmin(); };
  });
  Array.prototype.forEach.call(root.querySelectorAll('.team-rename'), function (b) {
    b.onclick = function () {
      var next = prompt('Rename team', b.getAttribute('data-name') || '');
      if (next && next.trim()) adminMutate('/api/admin/teams/' + encodeURIComponent(b.getAttribute('data-team')) + '/rename', { name: next.trim() }, err);
    };
  });
  Array.prototype.forEach.call(root.querySelectorAll('.team-delete'), function (b) {
    b.onclick = function () {
      if (!confirm('Delete this team? Members are unassigned; their sessions are unaffected.')) return;
      var id = b.getAttribute('data-team');
      if (openTeam === id) openTeam = null;
      adminMutate('/api/admin/teams/' + encodeURIComponent(id) + '/delete', {}, err);
    };
  });
  wireTeamDetail(root, err);
}

// The three session-visibility levels with the one-line explanation each shows
// in the picker. Shared by the admin team detail and the team-lead pane.
var VISIBILITY_LEVELS = [
  { v: 'owner', l: 'Only owners', d: 'Each member sees only their own sessions (default)' },
  { v: 'team', l: 'Everyone on this team', d: 'Team members see each other’s full transcripts' },
  { v: 'org', l: 'Everyone in the org', d: 'Any signed-in user sees this team’s full transcripts' },
];

// The visibility radio group. `current` is the team's level.
//
// Radios, not a <select>, on purpose: this is a DISCLOSURE control, so the
// consequence of each level must be readable BEFORE it is applied. A select only
// renders the chosen option's explanation, and since picking applies
// immediately (see wireVisibility), reading "org = any signed-in user sees this
// team's transcripts" would have required first widening to org. All three
// levels + descriptions now sit on screen at once and one click applies the one
// already read.
//
// `radioName` scopes the group: an admin's pane renders one control per team, and
// a shared name would make every team's radios one mutually-exclusive group.
function visibilityControl(current, radioName) {
  var items = VISIBILITY_LEVELS.map(function (o) {
    return '<label class="td-vis-item">' +
      '<input type="radio" name="' + esc(radioName) + '" class="td-vis-r" value="' + o.v + '"' +
      (o.v === current ? ' checked' : '') + ' />' +
      '<span class="td-vis-tx"><span class="td-vis-l">' + esc(o.l) + '</span>' +
      '<span class="td-vis-d">' + esc(o.d) + '</span></span>' +
      '</label>';
  }).join('');
  return '<div class="td-h">Session visibility</div>' +
    '<div class="td-vis">' + items + '</div>';
}

// Wire the visibility radios inside `wrap` to POST /api/team/visibility for
// `teamId`, then run `reload` on success. Shared by both panes (the route
// itself gates admin-any-team / lead-own-team, so one client path serves both).
//
// The POST fires on selection, so a FAILED write must put the dot back where it
// was: leaving it on the clicked level would show a widening that never
// happened — the worst way for a disclosure control to lie. `prev` is captured
// before the request and restored on any non-2xx or network error.
function wireVisibility(wrap, teamId, err, reload) {
  var radios = wrap.querySelectorAll('.td-vis-r');
  if (!radios.length) return;
  var prev = '';
  Array.prototype.forEach.call(radios, function (r: HTMLInputElement) {
    if (r.checked) prev = r.value;
  });
  var revert = function () {
    Array.prototype.forEach.call(radios, function (r: HTMLInputElement) {
      r.checked = r.value === prev;
    });
  };
  Array.prototype.forEach.call(radios, function (radio: HTMLInputElement) {
    radio.onchange = function () {
      if (!radio.checked) return;
      if (err) err.textContent = '';
      postStatus('/api/team/visibility', { teamId: teamId, level: radio.value }).then(function (r) {
        if (r.status >= 200 && r.status < 300) {
          prev = radio.value; // the write landed; this is the level to revert to next time
          reload();
        } else {
          revert();
          if (err) err.textContent = (r.body && r.body.error) || 'Failed.';
        }
      }).catch(function () {
        revert();
        if (err) err.textContent = 'Network error.';
      });
    };
  });
}

// A member row's role control (admin pane only). Two levels, not three: `admin`
// is an ORG-WIDE role, so offering it from inside one team's pane would imply the
// grant is team-scoped. An admin who happens to sit on a team renders as static
// text instead — the row must not offer a change that would silently demote them
// out of an org role, and a select showing "member/team-lead" for an admin would
// misstate what they are.
//
// Promotion lives on the member row (rather than a separate org-wide user list)
// because a lead is a lead OF a team: role and team are decided together, and the
// row already carries the team context. Per the settled decision, there is no
// one-lead-per-team rule — a team may have several leads or none — so this is a
// plain per-person control with no cross-row coupling.
function roleControl(m) {
  if (m.role === 'admin') return '<span class="td-role-fixed" title="Org-wide role — change it in the user list">admin</span>';
  var opt = function (v, l) {
    return '<option value="' + v + '"' + (m.role === v ? ' selected' : '') + '>' + l + '</option>';
  };
  return '<select class="td-role" data-user="' + esc(m.id) + '" data-prev="' + esc(m.role || 'member') + '"' +
    ' aria-label="Role for ' + esc(m.name || m.email) + '">' +
    opt('member', 'member') + opt('team-lead', 'team lead') + '</select>';
}

function teamDetail(detail, users) {
  var memberIds = {};
  (detail.members || []).forEach(function (m) { memberIds[m.id] = true; });
  var members = (detail.members || []).map(function (m) {
    return '<div class="td-row"><span class="td-who">' + esc(m.name || m.email) + '</span>' +
      roleControl(m) +
      '<button type="button" class="td-unassign" data-user="' + esc(m.id) + '">Remove</button></div>';
  }).join('') || '<div class="td-none">No members.</div>';
  // Users not already on this team, offered for assignment.
  var addable = users.filter(function (u) { return !memberIds[u.id]; });
  var addOpts = addable.map(function (u) { return '<option value="' + esc(u.id) + '">' + esc(u.email) + '</option>'; }).join('');
  var repos = (detail.repos || []).map(function (r) {
    return '<div class="td-row"><span class="td-repo">' + esc(r) + '</span>' +
      '<button type="button" class="td-repo-rm" data-repo="' + esc(r) + '">Remove</button></div>';
  }).join('') || '<div class="td-none">No repos mapped.</div>';

  return '<div class="team-detail" data-team="' + esc(detail.team.id) + '">' +
    '<div class="td-col"><div class="td-h">Members</div>' + members +
      (addable.length ? '<div class="td-add"><select class="td-user-sel">' + addOpts + '</select>' +
        '<button type="button" class="td-assign">Add</button></div>' : '') +
    '</div>' +
    '<div class="td-col"><div class="td-h">Repos</div>' + repos +
      '<div class="td-add"><input class="set-in td-repo-in" type="text" placeholder="owner/repo" />' +
        '<button type="button" class="td-repo-add">Add</button></div>' +
    '</div>' +
    '<div class="td-col">' + visibilityControl(detail.team.sessionVisibility || 'owner', 'vis-' + detail.team.id) + '</div>' +
    '</div>';
}

function wireTeamDetail(root, err) {
  var wrap = root.querySelector('.team-detail');
  if (!wrap) return;
  var teamId = wrap.getAttribute('data-team');
  Array.prototype.forEach.call(wrap.querySelectorAll('.td-unassign'), function (b) {
    b.onclick = function () { adminMutate('/api/admin/users/' + encodeURIComponent(b.getAttribute('data-user')) + '/team', { teamId: null }, err); };
  });
  var assign = wrap.querySelector('.td-assign');
  if (assign) (assign as HTMLElement).onclick = function () {
    var sel = wrap.querySelector('.td-user-sel') as HTMLSelectElement;
    if (sel && sel.value) adminMutate('/api/admin/users/' + encodeURIComponent(sel.value) + '/team', { teamId: teamId }, err);
  };
  // Role selects. Applied on change (no separate Save — same immediate-apply
  // shape as the visibility radios), and on failure the select SNAPS BACK to the
  // role the server still holds. Leaving it on the attempted value would show a
  // promotion that never happened; `data-prev` is the rendered truth, so revert
  // needs no closure state (the row is re-rendered from the server on success).
  //
  // The last-admin guard lives server-side (admin.ts) and surfaces here as the
  // 409's message — never re-derived on the client. This control can't hit it
  // anyway (admins render as static text), but a stale pane could, so the error
  // path is the same as every other mutation's.
  Array.prototype.forEach.call(wrap.querySelectorAll('.td-role'), function (sel: HTMLSelectElement) {
    sel.onchange = function () {
      var prev = sel.getAttribute('data-prev') || 'member';
      if (sel.value === prev) return;
      if (err) err.textContent = '';
      sel.disabled = true; // no second change in flight against a row about to re-render
      postStatus('/api/admin/users/' + encodeURIComponent(sel.getAttribute('data-user')) + '/role', { role: sel.value })
        .then(function (r) {
          if (r.status >= 200 && r.status < 300) {
            loadAdmin(); // repaints the pane from the server, so data-prev follows
          } else {
            sel.value = prev;
            sel.disabled = false;
            if (err) err.textContent = (r.body && r.body.error) || 'Failed.';
          }
        })
        .catch(function () {
          sel.value = prev;
          sel.disabled = false;
          if (err) err.textContent = 'Network error.';
        });
    };
  });
  Array.prototype.forEach.call(wrap.querySelectorAll('.td-repo-rm'), function (b) {
    b.onclick = function () { adminMutate('/api/admin/teams/' + encodeURIComponent(teamId) + '/repos/remove', { repo: b.getAttribute('data-repo') }, err); };
  });
  var repoAdd = wrap.querySelector('.td-repo-add');
  if (repoAdd) (repoAdd as HTMLElement).onclick = function () {
    var input = wrap.querySelector('.td-repo-in') as HTMLInputElement;
    var repo = (input.value || '').trim();
    if (repo) adminMutate('/api/admin/teams/' + encodeURIComponent(teamId) + '/repos', { repo: repo }, err);
  };
  wireVisibility(wrap, teamId, err, loadAdmin);
}

// ---- Team-lead: "My team" pane — the lead's own team only -------------------

// The lead's management surface, driving the /api/team/* routes (which gate
// server-side on lead-of-THIS-team): member add (teamless users only — the
// server 409s anyone already teamed, so the picker offers only the teamless),
// member remove is NOT offered (unassigning is admin-only — no /api/team route
// exists for it), repo add/remove, and the visibility toggle.
function loadMyTeam() {
  var root = $('#set-myteam');
  if (!root || !me || !me.teamId) return;
  Promise.all([
    get('/api/team/detail?teamId=' + encodeURIComponent(me.teamId)),
    loadOrgUsers(),
  ]).then(function (res) {
    var root2 = document.getElementById('set-myteam');
    if (!root2) return; // left Settings mid-fetch
    var detail = res[0];
    if (!detail || detail.error || !detail.team) {
      root2.innerHTML = '<div class="empty">Couldn’t load your team.</div>';
      return;
    }
    renderMyTeam(root2, detail, res[1] || []);
  }).catch(function () {
    var r = document.getElementById('set-myteam');
    if (r) r.innerHTML = '<div class="empty">Couldn’t load your team.</div>';
  });
}

function renderMyTeam(root, detail, orgUsers) {
  var members = (detail.members || []).map(function (m) {
    return '<div class="td-row"><span>' + esc(m.name || m.email) + '</span></div>';
  }).join('') || '<div class="td-none">No members.</div>';
  // Only teamless users are offered — the server rejects (409) anyone already
  // on another team, so don't offer what can only fail. The directory has no
  // team id, but teamName null ⇔ teamless, which is exactly the addable set.
  // Admins and other leads are NOT filtered out here: the directory carries no
  // role, and adding one to it would publish every user's role org-wide. The
  // server 409s ("only an admin can add a privileged account to a team") and
  // that message lands in #myteam-err, so the rare case fails visibly.
  var addable = orgUsers.filter(function (u) { return !u.teamName; });
  var addOpts = addable.map(function (u) {
    return '<option value="' + esc(u.id) + '">' + esc(u.name || u.email) + '</option>';
  }).join('');
  var repos = (detail.repos || []).map(function (r) {
    return '<div class="td-row"><span class="td-repo">' + esc(r) + '</span>' +
      '<button type="button" class="td-repo-rm" data-repo="' + esc(r) + '">Remove</button></div>';
  }).join('') || '<div class="td-none">No repos mapped.</div>';

  root.innerHTML =
    '<section class="panel">' +
      '<div class="panel-head"><h2>My team — ' + esc(detail.team.name) + '</h2></div>' +
      '<div class="team-detail" data-team="' + esc(detail.team.id) + '">' +
        '<div class="td-col"><div class="td-h">Members</div>' + members +
          (addable.length ? '<div class="td-add"><select class="td-user-sel">' + addOpts + '</select>' +
            '<button type="button" class="td-assign">Add</button></div>'
            : '<div class="td-none">No unassigned users to add.</div>') +
        '</div>' +
        '<div class="td-col"><div class="td-h">Repos</div>' + repos +
          '<div class="td-add"><input class="set-in td-repo-in" type="text" placeholder="owner/repo" />' +
            '<button type="button" class="td-repo-add">Add</button></div>' +
        '</div>' +
        '<div class="td-col">' + visibilityControl(detail.team.sessionVisibility || 'owner', 'myvis-' + detail.team.id) + '</div>' +
      '</div>' +
      '<div class="set-msg err" id="myteam-err"></div>' +
    '</section>';

  var err = document.getElementById('myteam-err');
  var wrap = root.querySelector('.team-detail');
  var teamId = detail.team.id;
  // Same mutate-then-reload shape as adminMutate, against the /api/team routes.
  var mutate = function (url, body) {
    if (err) err.textContent = '';
    postStatus(url, body).then(function (r) {
      if (r.status >= 200 && r.status < 300) {
        state.orgUsers = null; // membership changed — refetch the directory next time
        loadMyTeam();
      } else if (err) err.textContent = (r.body && r.body.error) || 'Failed.';
    }).catch(function () { if (err) err.textContent = 'Network error.'; });
  };
  var assign = wrap.querySelector('.td-assign');
  if (assign) (assign as HTMLElement).onclick = function () {
    var sel = wrap.querySelector('.td-user-sel') as HTMLSelectElement;
    if (sel && sel.value) mutate('/api/team/members', { teamId: teamId, userId: sel.value });
  };
  Array.prototype.forEach.call(wrap.querySelectorAll('.td-repo-rm'), function (b) {
    b.onclick = function () { mutate('/api/team/repos/remove', { teamId: teamId, repo: b.getAttribute('data-repo') }); };
  });
  var repoAdd = wrap.querySelector('.td-repo-add');
  if (repoAdd) (repoAdd as HTMLElement).onclick = function () {
    var input = wrap.querySelector('.td-repo-in') as HTMLInputElement;
    var repo = (input.value || '').trim();
    if (repo) mutate('/api/team/repos', { teamId: teamId, repo: repo });
  };
  wireVisibility(wrap, teamId, err, loadMyTeam);
}

// ---- Admin: global settings — email domain, LLM provider, ingest token ------

// Whether the ingest-token fold is open. Collapsed on every fresh entry to
// Settings (the secret shouldn't be on screen by default), but remembered across
// the re-render a rotate triggers — see paintFold in renderGlobal.
var tokenOpen = false;

function loadGlobal() {
  var root = $('#set-global');
  if (!root) return;
  get('/api/admin/settings').then(function (s) {
    if (!document.getElementById('set-global')) return; // left Settings mid-fetch
    renderGlobal(root, s || {});
  }).catch(function () { root.innerHTML = '<div class="empty">Couldn’t load settings.</div>'; });
}

function renderGlobal(root, s) {
  // The LLM key is never sent to the client — we only know whether one is set,
  // and the field's placeholder reflects that. A blank key field leaves the
  // stored key untouched (server-side); typing a value replaces it.
  root.innerHTML =
    '<section class="panel">' +
      '<div class="panel-head"><h2>Deployment settings</h2></div>' +
      '<div class="set-grid">' +
        '<label class="set-lbl">Signup email domain' +
          '<input class="set-in" id="gs-domain" type="text" value="' + esc(s.emailDomain || '') + '" placeholder="acme.com" /></label>' +
        '<label class="set-lbl">LLM provider' +
          '<input class="set-in" id="gs-provider" type="text" value="' + esc(s.llmProvider || '') + '" placeholder="anthropic" /></label>' +
        '<label class="set-lbl">LLM model (optional)' +
          '<input class="set-in" id="gs-model" type="text" value="' + esc(s.llmModel || '') + '" placeholder="provider default" /></label>' +
        '<label class="set-lbl">LLM heavy model (optional)' +
          '<input class="set-in" id="gs-model-heavy" type="text" value="' + esc(s.llmModelHeavy || '') + '" placeholder="provider default" /></label>' +
        '<label class="set-lbl">LLM API key' +
          '<input class="set-in" id="gs-key" type="password" placeholder="' + (s.llmApiKeySet ? '•••••• (set — leave blank to keep)' : 'not set') + '" /></label>' +
        // Header-auth gateways (provider `openai-compatible-nokey`): a JSON object of
        // request headers replaces the API key as the credential. Same
        // secret round-trip rules as the key: never echoed, blank keeps current.
        '<label class="set-lbl">LLM base URL (optional)' +
          '<input class="set-in" id="gs-baseurl" type="text" value="' + esc(s.llmBaseUrl || '') + '" placeholder="https://gateway.internal/v1" /></label>' +
        '<label class="set-lbl">LLM request headers (optional, JSON)' +
          '<input class="set-in" id="gs-headers" type="password" placeholder="' + (s.llmHeadersSet ? '•••••• (set — leave blank to keep)' : '{"x-api-key": "…"}') + '" /></label>' +
      '</div>' +
      '<div class="set-actions"><button type="button" class="set-btn" id="gs-save">Save settings</button>' +
        '<span class="set-msg" id="gs-msg"></span></div>' +
    '</section>' +
    // The ingest token is a live deployment SECRET rendered in plaintext, so the
    // panel is collapsed by default: it's set once and rarely revisited, and
    // nobody should have it on screen (or in a screen-share) just for having
    // opened Settings. Reuses the .fc-head caret idiom from the file collapser.
    '<section class="panel gs-fold collapsed" id="gs-token-panel">' +
      '<button type="button" class="panel-head gs-fold-head" id="gs-token-toggle" aria-expanded="false" aria-controls="gs-token-body">' +
        '<span class="fc-caret">▾</span><h2>Ingest token</h2>' +
        '<span class="gs-fold-hint">hidden — click to reveal</span>' +
      '</button>' +
      '<div class="gs-fold-body" id="gs-token-body" hidden>' +
        '<p class="set-note">Distribute this to developers via the install one-liner. Rotating keeps the old token valid for 24h.</p>' +
        '<div class="gs-token" id="gs-token">' + esc(s.ingestToken || '(none)') + '</div>' +
        '<div class="set-actions"><button type="button" class="set-btn" id="gs-rotate">Rotate token</button>' +
          '<span class="set-msg" id="gs-token-msg"></span></div>' +
      '</div>' +
    '</section>';

  var msg = document.getElementById('gs-msg');
  (document.getElementById('gs-save') as HTMLElement).onclick = function () {
    var domain = (document.getElementById('gs-domain') as HTMLInputElement).value.trim();
    // Clearing a previously-set domain opens signup to any address — a
    // security-relevant change, so confirm it rather than let a stray blank
    // field disable the gate on a Save meant for other fields.
    if (!domain && s.emailDomain &&
        !confirm('Clear the signup email domain? Anyone will be able to sign up, from any email domain.')) {
      return;
    }
    var patch: Record<string, string> = {
      emailDomain: domain,
      llmProvider: (document.getElementById('gs-provider') as HTMLInputElement).value.trim(),
      llmModel: (document.getElementById('gs-model') as HTMLInputElement).value.trim(),
      llmModelHeavy: (document.getElementById('gs-model-heavy') as HTMLInputElement).value.trim(),
      llmBaseUrl: (document.getElementById('gs-baseurl') as HTMLInputElement).value.trim(),
    };
    // Only include the secrets when the admin typed one — blank means "keep current".
    var key = (document.getElementById('gs-key') as HTMLInputElement).value;
    if (key) patch.llmApiKey = key;
    var headers = (document.getElementById('gs-headers') as HTMLInputElement).value;
    if (headers) patch.llmHeaders = headers;
    if (msg) msg.textContent = '';
    postStatus('/api/admin/settings', patch).then(function (r) {
      if (r.status >= 200 && r.status < 300) { loadGlobal(); }
      else if (msg) { msg.textContent = (r.body && r.body.error) || 'Failed.'; msg.className = 'set-msg err'; }
    }).catch(function () { if (msg) { msg.textContent = 'Network error.'; msg.className = 'set-msg err'; } });
  };

  // Fold toggle. `tokenOpen` is module state, not DOM state, because a rotate
  // calls loadGlobal() → renderGlobal() and rebuilds this markup: without it the
  // panel would slam shut exactly when the admin wants to read the NEW token.
  var tPanel = document.getElementById('gs-token-panel');
  var tBody = document.getElementById('gs-token-body');
  var tToggle = document.getElementById('gs-token-toggle');
  var paintFold = function () {
    if (!tPanel || !tBody || !tToggle) return;
    tPanel.classList.toggle('collapsed', !tokenOpen);
    (tBody as HTMLElement).hidden = !tokenOpen;
    tToggle.setAttribute('aria-expanded', tokenOpen ? 'true' : 'false');
    var hint = tToggle.querySelector('.gs-fold-hint');
    if (hint) hint.textContent = tokenOpen ? '' : 'hidden — click to reveal';
  };
  if (tToggle) tToggle.onclick = function () { tokenOpen = !tokenOpen; paintFold(); };
  paintFold(); // re-apply the remembered state after a re-render

  var tmsg = document.getElementById('gs-token-msg');
  (document.getElementById('gs-rotate') as HTMLElement).onclick = function () {
    if (!confirm('Rotate the ingest token? The current token stays valid for 24h; after that every machine must re-run the installer.')) return;
    postStatus('/api/admin/settings/rotate-token', {}).then(function (r) {
      if (r.status >= 200 && r.status < 300) loadGlobal();
      else if (tmsg) { tmsg.textContent = (r.body && r.body.error) || 'Failed.'; tmsg.className = 'set-msg err'; }
    }).catch(function () { if (tmsg) { tmsg.textContent = 'Network error.'; tmsg.className = 'set-msg err'; } });
  };
}

// ---- Admin: Jira connector (AL-127) -----------------------------------------
//
// The panel that makes the connector configurable at all. Until it existed,
// `connectors.jira` was `{}` in any real deployment, so `jiraFromSettings`
// returned null, `fetchDetails` exited on its first line, and every merely-
// mentioned ticket key carried no title, status or story points — with nothing
// anywhere saying why.
//
// Two design constraints shape the markup:
//
//  * The panel is a DIAGNOSTIC first and a form second. Both tiers fail silently
//    and independently (design §5), so each gets its own status line naming what
//    is missing and what that costs. `connector-status.ts` holds those readings
//    as pure functions.
//  * It deliberately omits a project→team editor. The field is still stored and
//    validated, but nothing reads it (design §6, §5b — a ticket reaches a team
//    through the sessions on it), and a control that changes no behaviour is
//    worse than no control: it invites an admin to configure scoping that will
//    not happen. AL-127's scope list predates that decision; §8 is the later
//    word and this follows §8. (There is no backlog-context toggle either, for a
//    different reason: that pool is unconditional wherever Jira is configured —
//    see docs/plans/backlog-ticket-context.md D8. Nothing to switch.)
//
// The secret needs no module state: the GET returns `webhookPath`, which
// necessarily contains it (a URL you cannot read is not a URL you can paste), so
// the panel can always rebuild the URL from the server. The bare token has no
// such reason to reappear and never does.
var jiraStepsOpen = false;

function loadJira() {
  var root = $('#set-jira');
  if (!root) return;
  get('/api/settings/connectors/jira').then(function (c) {
    if (!document.getElementById('set-jira')) return; // left Settings mid-fetch
    // A member/lead never reaches loadJira, but a 403 body would otherwise
    // render as an all-null connector reading "not configured" — a lie.
    if (!c || c.error) { renderJiraInfo(); return; }
    renderJira(root, c as PublicJira);
  }).catch(function () { root.innerHTML = '<div class="empty">Couldn’t load the Jira connector.</div>'; });
}

// Non-admins: state what the connector does and who can change it, and say
// nothing about whether it is configured. The GET is admin-only, so this pane
// makes no request — a member learning "site URL set, no token" would be an
// information leak for no benefit, since they cannot act on it.
function renderJiraInfo() {
  var root = document.getElementById('set-jira');
  if (!root) return;
  root.innerHTML =
    '<section class="panel">' +
      '<div class="panel-head"><h2>Jira</h2></div>' +
      '<p class="set-note">Ticket keys your sessions touch (AL-123, and keys in branch or commit ' +
        'messages) become ticket artifacts automatically — no setup on your side. An admin ' +
        'connects the Jira site itself, which is what fills in each ticket’s title, status and ' +
        'story points and keeps them current.</p>' +
    '</section>';
}

function renderJira(root, c: PublicJira) {
  var wh = webhookHealth(c, Date.now());
  var rest = restHealth(c);
  var origin = window.location.origin;
  var url = webhookUrl(origin, c.webhookPath);
  var reachable = reachableFromJira(origin);

  root.innerHTML =
    '<section class="panel">' +
      '<div class="panel-head"><h2>Jira connector</h2></div>' +
      '<p class="set-note">Sessions discover tickets on their own. This connects the Jira site so ' +
        'those tickets carry a title and a live status: <b>webhooks</b> keep status current, and an ' +
        '<b>API token</b> fetches details and drops keys that don’t exist.</p>' +

      // Both tiers' readings, above the form: the answer to "is this working"
      // must not require reading the fields and inferring it.
      '<div class="jc-status">' +
        jiraStatusRow('Webhooks', 'ticket status stays current', wh) +
        jiraStatusRow('API token', 'titles, statuses, story points', rest) +
      '</div>' +

      '<div class="set-grid">' +
        '<label class="set-lbl">Jira site URL' +
          '<input class="set-in" id="jc-site" type="text" value="' + esc(c.siteUrl || '') + '" placeholder="https://acme.atlassian.net" /></label>' +
        '<label class="set-lbl">Jira account email' +
          '<input class="set-in" id="jc-email" type="text" value="' + esc(c.apiEmail || '') + '" placeholder="tuneloop-bot@acme.com" /></label>' +
        '<label class="set-lbl">API token' +
          '<input class="set-in" id="jc-token" type="password" autocomplete="off" placeholder="' +
            (c.apiTokenSet ? '•••••• (set — leave blank to keep)' : 'not set') + '" />' +
          // A blank field means "keep" (the GET never returned the value), so
          // removing a token needs its own control or it would be unremovable
          // from the UI — the route takes an explicit null for exactly this.
          (c.apiTokenSet ? '<button type="button" class="jc-clear" id="jc-token-clear">Remove stored token</button>' : '') +
        '</label>' +
      '</div>' +
      // Staff churn is the accepted cost of API-token auth over OAuth (design
      // §5a), and the doc says to document it here rather than in a wiki nobody
      // opens: a personal token dies with the person's account.
      '<p class="set-note jc-hint">Use a <b>service account</b>’s token, not your own — a personal ' +
        'token stops working when that person’s Jira account is deactivated. ' +
        '<a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener">Create a token</a>.</p>' +
      '<div class="set-actions"><button type="button" class="set-btn" id="jc-save">Save connector</button>' +
        '<span class="set-msg" id="jc-msg"></span></div>' +

      // Repair links on tickets stored before the site URL was known (AL-166).
      // Shown only once a site URL exists, since there is nothing to build from
      // otherwise. Saving a site URL for the FIRST time already backfills, so this
      // is for the order that trigger cannot serve — connector configured first,
      // sessions ingested after, which is every fresh deployment.
      (c.siteUrl
        ? '<div class="jc-relink">' +
            '<p class="set-note">Tickets found before the site URL was set have no link to Jira. ' +
              'This fills them in; tickets found from now on are linked as they arrive.</p>' +
            '<div class="set-actions">' +
              '<button type="button" class="jc-btn2" id="jc-relink">Fix missing ticket links</button>' +
              '<span class="set-msg" id="jc-relink-msg"></span>' +
            '</div>' +
          '</div>'
        : '') +

      '<div class="jc-hook">' +
        '<div class="td-h">Webhook</div>' +
        (url
          ? '<p class="set-note">Paste this into Jira as the webhook URL. The secret <i>is</i> the ' +
              'credential, so treat the whole URL as one.</p>' +
            '<div class="gs-token jc-url" id="jc-url">' + esc(url) + '</div>' +
            (reachable ? '' :
              // The first-run failure that looks like nothing: a well-formed URL
              // Jira's servers cannot route to. Jira reports the failure on its
              // own screen, and this panel would sit at "waiting" forever.
              '<p class="set-note jc-warn">' + esc(origin) + ' is a local or private address, so ' +
              'Jira’s servers can’t reach it — deliveries will fail on Jira’s side with nothing ' +
              'shown here. Expose this server publicly (or via a tunnel) and use that URL instead.</p>') +
            '<div class="set-actions">' +
              '<button type="button" class="set-btn" id="jc-copy">Copy URL</button>' +
              '<button type="button" class="jc-btn2" id="jc-regen">Regenerate secret</button>' +
              '<span class="set-msg" id="jc-hook-msg"></span>' +
            '</div>'
          : '<p class="set-note">No webhook secret yet. Generating one produces the URL to paste ' +
              'into Jira; it is shown once and never readable again.</p>' +
            '<div class="set-actions">' +
              '<button type="button" class="set-btn" id="jc-regen">Generate secret</button>' +
              '<span class="set-msg" id="jc-hook-msg"></span>' +
            '</div>') +
      '</div>' +

      // Collapsed by default: it is a one-time read, and an admin returning to
      // check `lastWebhookAt` should not have to scroll past it.
      '<div class="jc-steps' + (jiraStepsOpen ? '' : ' collapsed') + '" id="jc-steps">' +
        '<button type="button" class="gs-fold-head jc-steps-head" id="jc-steps-toggle"' +
          ' aria-expanded="' + (jiraStepsOpen ? 'true' : 'false') + '" aria-controls="jc-steps-body">' +
          '<span class="fc-caret">▾</span><h2>Setup steps</h2>' +
        '</button>' +
        '<div class="jc-steps-body" id="jc-steps-body"' + (jiraStepsOpen ? '' : ' hidden') + '>' +
          '<ol class="jc-ol">' +
            '<li>Enter the site URL above (<code>https://your-site.atlassian.net</code>) and save.</li>' +
            '<li>Create an API token for a Jira <b>service account</b>, enter that account’s email ' +
              'and the token above, and save. This is what fills in ticket titles and statuses.</li>' +
            '<li>Generate the webhook secret above and copy the URL.</li>' +
            '<li>In Jira: <b>Settings → System → WebHooks → Create a WebHook</b>. Paste the URL, ' +
              'and under <b>Issue</b> tick <b>created</b> and <b>updated</b>. Leave ' +
              '<b>deleted</b> off — it is ignored here. A JQL filter is optional: events for ' +
              'tickets no session has touched are dropped cheaply.</li>' +
            '<li>Change any issue in Jira. <b>Webhooks</b> above should read “last event a few ' +
              'seconds ago”.</li>' +
          '</ol>' +
          '<p class="set-note">Tickets are only ever discovered from transcripts — Jira never ' +
            'introduces a ticket here, it only refreshes one a session already named.</p>' +
        '</div>' +
      '</div>' +
    '</section>';

  wireJira(c);
}

// One tier's reading: what it is, what it buys, and a dot whose colour is the
// whole message at a glance.
function jiraStatusRow(label, buys, health) {
  return '<div class="jc-row jc-' + esc(health.level) + '">' +
    '<span class="jc-dot"></span>' +
    '<span class="jc-k">' + esc(label) + '</span>' +
    '<span class="jc-tx"><span class="jc-t">' + esc(health.text) + '</span>' +
      '<span class="jc-b">' + esc(buys) + '</span></span>' +
    '</div>';
}

function wireJira(c: PublicJira) {
  var msg = document.getElementById('jc-msg');
  var hookMsg = document.getElementById('jc-hook-msg');

  var steps = document.getElementById('jc-steps');
  var stepsBody = document.getElementById('jc-steps-body');
  var stepsToggle = document.getElementById('jc-steps-toggle');
  if (stepsToggle) stepsToggle.onclick = function () {
    jiraStepsOpen = !jiraStepsOpen;
    if (steps) steps.classList.toggle('collapsed', !jiraStepsOpen);
    if (stepsBody) (stepsBody as HTMLElement).hidden = !jiraStepsOpen;
    stepsToggle.setAttribute('aria-expanded', jiraStepsOpen ? 'true' : 'false');
  };

  (document.getElementById('jc-save') as HTMLElement).onclick = function () {
    var site = (document.getElementById('jc-site') as HTMLInputElement).value.trim();
    var email = (document.getElementById('jc-email') as HTMLInputElement).value.trim();
    var token = (document.getElementById('jc-token') as HTMLInputElement).value;
    // Clearing the site URL turns the whole connector off — both tiers read it —
    // so it gets the same confirm the email-domain field gets, rather than
    // letting a stray blank disable ticket enrichment on an unrelated save.
    if (!site && c.siteUrl &&
        !confirm('Clear the Jira site URL? Both webhooks and detail fetch stop, and new tickets will have no title or status.')) {
      return;
    }
    // siteUrl/apiEmail send '' (the route reads a blank as an explicit clear, so
    // emptying a field in the form empties it in storage — these are visible in
    // the GET, so what is on screen is the truth being saved). The TOKEN is the
    // opposite: the GET never returned it, so a blank field means "unchanged"
    // and is omitted entirely. Clearing a token is `null`, from Remove below.
    var patch: Record<string, unknown> = { siteUrl: site, apiEmail: email };
    if (token) patch.apiToken = token;
    if (msg) { msg.textContent = 'Saving…'; msg.className = 'set-msg'; }
    putStatus('/api/settings/connectors/jira', patch).then(function (r) {
      if (r.status >= 200 && r.status < 300) {
        // The one-time backfill: existing jira rows were written by the
        // transcript path, which cannot know the site origin, so the first
        // save is when they get a browse link. Worth saying — it is the only
        // visible effect of that save on data already in the store.
        var n = r.body && r.body.backfilled;
        loadJira();
        var after = document.getElementById('jc-msg');
        if (after) {
          after.textContent = n ? 'Saved. Linked ' + n + ' existing ticket' + (n === 1 ? '' : 's') + ' to this site.' : 'Saved.';
          after.className = 'set-msg ok';
        }
      } else if (msg) { msg.textContent = (r.body && r.body.error) || 'Failed.'; msg.className = 'set-msg err'; }
    }).catch(function () { if (msg) { msg.textContent = 'Network error.'; msg.className = 'set-msg err'; } });
  };

  var clearTok = document.getElementById('jc-token-clear');
  if (clearTok) clearTok.onclick = function () {
    if (!confirm('Remove the stored API token? Ticket titles and statuses stop being fetched, and bogus ticket keys stop being discarded.')) return;
    if (msg) { msg.textContent = ''; msg.className = 'set-msg'; }
    putStatus('/api/settings/connectors/jira', { apiToken: null }).then(function (r) {
      if (r.status >= 200 && r.status < 300) loadJira();
      else if (msg) { msg.textContent = (r.body && r.body.error) || 'Failed.'; msg.className = 'set-msg err'; }
    }).catch(function () { if (msg) { msg.textContent = 'Network error.'; msg.className = 'set-msg err'; } });
  };

  var copy = document.getElementById('jc-copy');
  if (copy) copy.onclick = function () {
    var el = document.getElementById('jc-url');
    var text = el ? el.textContent || '' : '';
    var ok = function () { if (hookMsg) { hookMsg.textContent = 'Copied.'; hookMsg.className = 'set-msg ok'; } };
    // Clipboard access needs a secure context, which plain-HTTP localhost is
    // not — exactly where an admin first tries this. Falling back to selecting
    // the URL means ⌘C still works instead of the button doing nothing.
    var fallback = function () {
      if (el && window.getSelection) {
        var range = document.createRange();
        range.selectNodeContents(el);
        var sel = window.getSelection();
        if (sel) { sel.removeAllRanges(); sel.addRange(range); }
      }
      if (hookMsg) { hookMsg.textContent = 'Selected — press ⌘C / Ctrl-C to copy.'; hookMsg.className = 'set-msg'; }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(ok).catch(fallback);
    } else fallback();
  };

  var regen = document.getElementById('jc-regen');
  if (regen) regen.onclick = function () {
    // Regenerating kills the URL already in Jira — silent breakage (Jira keeps
    // POSTing to a URL that now 401s) so it is confirmed. Generating a FIRST
    // secret breaks nothing and isn't.
    if (c.webhookSecretSet &&
        !confirm('Regenerate the webhook secret? The URL currently in Jira stops working immediately and you must paste the new one.')) {
      return;
    }
    if (hookMsg) { hookMsg.textContent = 'Generating…'; hookMsg.className = 'set-msg'; }
    postStatus('/api/settings/connectors/jira/generate-secret', {}).then(function (r) {
      if (r.status >= 200 && r.status < 300) {
        // The minted secret is not kept: loadJira's GET returns `webhookPath`,
        // which carries it, so the repaint shows the same URL the POST returned.
        loadJira();
        var after = document.getElementById('jc-hook-msg');
        if (after) { after.textContent = 'New URL below — paste it into Jira now.'; after.className = 'set-msg ok'; }
      } else if (hookMsg) { hookMsg.textContent = (r.body && r.body.error) || 'Failed.'; hookMsg.className = 'set-msg err'; }
    }).catch(function () { if (hookMsg) { hookMsg.textContent = 'Network error.'; hookMsg.className = 'set-msg err'; } });
  };

  // Fix missing ticket links (AL-166). No confirm: it only fills NULLs, never
  // rewrites a link, and skips keys Jira says don't exist — there is nothing to
  // undo. Deliberately does NOT repaint, unlike the handlers above: the connector
  // fields are unchanged, and a repaint would wipe the count the admin just asked
  // for. Zero gets its own wording — "linked 0" reads as a failure when it
  // actually means every ticket already had a link.
  var relink = document.getElementById('jc-relink');
  var relinkMsg = document.getElementById('jc-relink-msg');
  if (relink) relink.onclick = function () {
    if (relinkMsg) { relinkMsg.textContent = 'Linking…'; relinkMsg.className = 'set-msg'; }
    postStatus('/api/settings/connectors/jira/backfill-links', {}).then(function (r) {
      if (!relinkMsg) return;
      if (r.status >= 200 && r.status < 300) {
        var n = (r.body && r.body.backfilled) || 0;
        relinkMsg.textContent = n
          ? 'Linked ' + n + ' ticket' + (n === 1 ? '' : 's') + ' to this site.'
          : 'Nothing to fix — every ticket already has a link.';
        relinkMsg.className = 'set-msg ok';
      } else { relinkMsg.textContent = (r.body && r.body.error) || 'Failed.'; relinkMsg.className = 'set-msg err'; }
    }).catch(function () { if (relinkMsg) { relinkMsg.textContent = 'Network error.'; relinkMsg.className = 'set-msg err'; } });
  };
}
